import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format, isToday, isYesterday } from "date-fns";
import {
  BellRing,
  X,
  ExternalLink,
  FileText,
  Music,
  CheckCheck,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  Loader2,
  Send,
  Flame,
  Heart,
} from "lucide-react";

import {
  getReceivedNotifications,
  dismissNotification,
  respondToNotification,
  dismissAllNotifications,
  setEventChatMute,
} from "../features/user/userActions";
import { replyToGlitchNotify } from "../features/glitch/glitchActions";
import { answerBusterCall, beLameBusterCall } from "../features/busterCall/busterCallActions";
import AnswerSteps from "./content/busterCall/AnswerSteps";
import { triggerHaptic } from "../utils/haptics";
import { answeredLabel } from "../lib/notificationAnswerLabel";
import { toast } from "sonner";

// Feature #11 — in-portal notification banner.
//
// Shows the signed-in user's UN-dismissed notifications stacked at the top of the
// app (newest first) until they click the X. Web push ("pops on their phone") is
// the other delivery channel but only reaches devices that enabled push; this
// banner is the always-on in-portal channel, so a notification is never missed
// just because a device wasn't subscribed. Dismissal is server-side
// (POST /notification/portal/dismiss) so closing it on one device clears it on
// all of them, and the full history always stays on the Notifications page.
//
// Live cross-session sync (Glitch #397, 2026-09-02): dismissal/response is
// stored on the server, but each browser only learned about it on its own poll
// (formerly 60s), so a second browser/device kept showing a banner that was
// already dismissed elsewhere and Levi had to X it more than once. Two levers:
//  (1) BroadcastChannel — tabs of this origin + browser profile share no other
//      state, so a dismiss/respond in one tab posts a message and sibling tabs
//      (the "two Gmail tabs" case) mirror it instantly.
//  (2) Visibility-aware polling — while the tab is actually on screen we poll at
//      POLL_MS_VISIBLE so a DIFFERENT browser/device (no shared channel) picks
//      up the server-side change within a few seconds; hidden tabs stop polling
//      and reload the moment they become visible again. The old 60s blind poll
//      is what made cross-device dismissal feel like it "wasn't linked".
//
// Notification "center" stack (Glitch #404, 2026-09-02 — Levi): when several
// banners are up at once the stack is treated like a phone notification group
// so it can never cover the screen. The panel shows the NEWEST 3 expanded; the
// rest sit behind a "+N more" expander (up to MAX_INLINE_REVEAL more inline —
// beyond that, "See all" opens the grouped Notifications page). When 2+ are up,
// a slim header bar above the stack carries a live count, a one-tap "Clear all"
// (POST /notification/portal/dismiss-all) and an "Edit settings" button that
// deep-links to /p/notifications?tab=settings. Rows drop in with a short
// animation and every dismiss/expand tap fires a light haptic pulse where the
// device supports it (iOS Safari ignores navigator.vibrate — the press state is
// the feedback there, so every action also scales down on :active).

const SYNC_CHANNEL = "kindred-notif-banner-sync-v1";
const POLL_MS_VISIBLE = 8 * 1000; // live cadence while the tab is on screen
const MAX_VISIBLE = 3; // newest N banners shown expanded at once
const MAX_INLINE_REVEAL = 10; // reveal up to N more inline; beyond → "See all"

// Buster Call notification detection — matches buster_call:N or buster_call:N:rebroadcast
const isBusterCall = (ref) => typeof ref === "string" && ref.startsWith("buster_call:");

const formatWhen = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const time = format(d, "h:mm a");
  if (isToday(d)) return `Today ${time}`;
  if (isYesterday(d)) return `Yesterday ${time}`;
  return format(d, "MMM d, h:mm a");
};

// Compact thumbnail for banner. Clicking opens the media in a new tab.
// Kept smaller than the NotificationInbox equivalent (h-10/w-10) to fit the
// slim banner strip without crowding the title/body.
const BannerMediaThumb = ({ item }) => {
  const { url, name, category } = item;
  const base =
    "h-10 w-10 shrink-0 rounded-md border border-zinc-700 overflow-hidden flex items-center justify-center bg-zinc-800 hover:border-cyan-400/60 transition-colors";
  if (category === "image") {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={base} title={name}>
        <img src={url} alt={name || "Image"} className="h-full w-full object-cover" />
      </a>
    );
  }
  if (category === "video") {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={base} title={name}>
        <video src={url} className="h-full w-full object-cover" muted playsInline preload="metadata" />
      </a>
    );
  }
  if (category === "audio") {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={`${base} flex-col gap-0.5`} title={name}>
        <Music className="h-4 w-4 text-cyan-400" />
        <span className="text-[8px] text-zinc-400 line-clamp-1 px-0.5">{name || "Audio"}</span>
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`${base} flex-col gap-0.5`} title={name}>
      <FileText className="h-4 w-4 text-cyan-400" />
      <span className="text-[8px] text-zinc-400 line-clamp-1 px-0.5">{category === "pdf" ? "PDF" : "File"}</span>
    </a>
  );
};

// Extract the glitch id from a two-way-conversation ref "glitch_notify:42" → 42.
// Only "glitch_notify:" triggers the Respond input — other glitch refs
// (glitch:N for status/report/reply notifications) don't offer a reply path.
// Thread #1338.
const glitchIdFromRef = (ref) => {
  if (!ref || typeof ref !== "string") return null;
  const m = ref.match(/^glitch_notify:(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
};

// Extract the buster call integer id from a ref like "buster_call:7" or "buster_call:7:rebroadcast"
const busterCallIdFromRef = (ref) => {
  if (!ref || typeof ref !== "string") return null;
  const m = ref.match(/^buster_call:(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};

const NotificationBanner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const [items, setItems] = useState([]);
  const [revealOpen, setRevealOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  // Per-notification inline reply state: { [notifId]: { text, sent, sending } }
  const [replyState, setReplyState] = useState({});
  // Buster Call notification expansion state: 0=normal, 1=first dismiss blocked, 2=first Be Lame
  const [busterExpand, setBusterExpand] = useState({});
  // Per-notification proof upload state: { [notifId]: { submitting: bool } }
  const [busterProofState, setBusterProofState] = useState({});
  // Which buster rows the employee has tapped "Open" on in this mount, so the
  // walkthrough can tick step 1. Session-local by design: the portal has no
  // record of whether a review was actually left, and claiming one would be a
  // lie — this only confirms the employee was sent to the review page.
  const [busterOpened, setBusterOpened] = useState({});
  // Touch tracking for swipe-to-expand on buster call rows (keyed by notif id)
  const busterTouchRef = useRef({});
  // Hidden file input refs for direct proof upload from "Show Support" (keyed by notif id)
  const busterFileRefs = useRef({});
  // Same-browser-profile tab bus. Fires only on OTHER tabs of this origin —
  // BroadcastChannel never delivers to the context that posted.
  const channelRef = useRef(null);
  const channelReady = useRef(false);

  const postSync = useCallback((msg) => {
    try {
      if (channelReady.current && channelRef.current) channelRef.current.postMessage(msg);
    } catch (_) { /* channel closed or blocked — polling still covers the tab */ }
  }, []);

  // Navigate to the notification URL. Same-origin portal URLs use React Router
  // so the user stays in the SPA without a new-tab hop (which silently fails on
  // mobile/PWA). External URLs fall back to window.open in a new tab.
  const handleOpen = useCallback((url) => {
    try {
      const u = new URL(url);
      if (u.origin === window.location.origin) {
        navigate(u.pathname + u.search + u.hash);
        return;
      }
    } catch (_) {}
    window.open(url, "_blank", "noopener,noreferrer");
  }, [navigate]);

  const load = useCallback(() => {
    if (!token) return;
    dispatch(getReceivedNotifications())
      .then((res) => {
        const rows = Array.isArray(res.payload?.data) ? res.payload.data : [];
        setItems(rows.filter((n) => !n.dismissed_at));
      })
      .catch(() => {});
  }, [dispatch, token]);

  // Open one BroadcastChannel for the banner's lifetime and answer sibling-tab
  // messages: a dismissal, response, or clear-all that happened in ANOTHER tab
  // of this browser is applied here immediately (no poll wait).
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.BroadcastChannel === "undefined") {
      return undefined;
    }
    const channel = new BroadcastChannel(SYNC_CHANNEL);
    channelRef.current = channel;
    channelReady.current = true;
    const onMessage = (e) => {
      const msg = e && e.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "dismiss") {
        setItems((prev) => prev.filter((x) => x.id !== msg.id));
      } else if (msg.type === "respond") {
        setItems((prev) =>
          prev.map((x) => (x.id === msg.id ? { ...x, responded_value: msg.value } : x)),
        );
      } else if (msg.type === "dismiss-all") {
        setItems([]);
        setRevealOpen(false);
      }
    };
    channel.addEventListener("message", onMessage);
    return () => {
      channelReady.current = false;
      channel.removeEventListener("message", onMessage);
      try { channel.close(); } catch (_) {}
      channelRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setItems([]);
      return undefined;
    }
    let pollId = null;
    const startPoll = () => {
      if (pollId) clearInterval(pollId);
      pollId = setInterval(load, POLL_MS_VISIBLE);
    };
    const stopPoll = () => {
      if (pollId) {
        clearInterval(pollId);
        pollId = null;
      }
    };
    // Poll only while the tab is actually visible; a hidden tab is throttled by
    // the browser anyway and gains nothing from firing. Coming back to the tab
    // reloads instantly so the banner is current before the user can act on it.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        load();
        startPoll();
      } else {
        stopPoll();
      }
    };
    const onFocus = () => load();
    load();
    startPoll();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    // Let in-app actions force an immediate refetch (e.g. completing a challenge
    // on the Balance page auto-dismisses its banner server-side; without this we
    // wouldn't reflect it until the next poll). Same-tab actions don't fire
    // "focus", so this is the only prompt the banner gets.
    window.addEventListener("notifications:refresh", load);
    return () => {
      stopPoll();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("notifications:refresh", load);
    };
  }, [token, load]);

  const handleDismiss = (n) => {
    if (isBusterCall(n.ref)) {
      // Buster Call: X expands to "Show Support or Be Lame" instead of dismissing.
      triggerHaptic(30);
      setBusterExpand((prev) => ({ ...prev, [n.id]: 1 }));
      return;
    }
    // Optimistically remove from the banner; the row still lives on the
    // Notifications page, and the server call marks it dismissed everywhere.
    triggerHaptic(12);
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    dispatch(dismissNotification({ id: n.id }))
      .then(() => postSync({ type: "dismiss", id: n.id }))
      .catch(() => load());
  };

  // "Turn off / Turn on notifications from Event" (Levi 2026-11-04). Muting
  // closes this event's open banners on the server, so drop them here too.
  const handleEventChatMute = (n) => {
    const muted = !n.event_chat_muted;
    triggerHaptic(12);
    setItems((prev) => (muted
      ? prev.filter((x) => x.event_chat_host_event_id !== n.event_chat_host_event_id)
      : prev.map((x) => (x.id === n.id ? { ...x, event_chat_muted: false } : x))));
    dispatch(setEventChatMute({ id: n.id, muted }))
      .then(() => load())
      .catch(() => load());
  };

  // Second "Be Lame" tap — the employee has explicitly declined this call.
  //
  // This must NOT go through the generic dismissNotification (Levi 2026-10-05,
  // thread 1a10cf494be4ccac). The row the server injects for an unanswered call
  // has a synthetic id ("buster_call_inject_7"), which the generic dismiss
  // endpoint parses with parseInt → NaN → no-op; and even a real row id would
  // not have held, because the server re-injects a banner for every unanswered
  // call on every poll, so the notification popped straight back up no matter
  // how many times it was declined. The decline route records the choice
  // server-side, which is what makes "Be Lame" actually close it out.
  //
  // Idempotent: the endpoint returns the same answer for a repeat tap, so the
  // multiple taps Levi describes are all safe. A genuine failure restores the
  // row from the server rather than leaving a banner that looks closed but isn't.
  const handleBusterCallDismiss = (n) => {
    const callId = busterCallIdFromRef(n.ref);
    if (!callId) {
      toast.error("Couldn't close that Buster Call notification. Please try again.");
      return;
    }
    triggerHaptic(100);
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    setBusterExpand((prev) => { const s = { ...prev }; delete s[n.id]; return s; });
    dispatch(beLameBusterCall({ id: callId }))
      .then((res) => {
        if (beLameBusterCall.rejected.match(res)) throw new Error("decline failed");
        postSync({ type: "dismiss", id: n.id });
      })
      .catch(() => {
        toast.error("Couldn't close that Buster Call notification. Please try again.");
        load();
      });
  };

  const handleShowSupport = (n) => {
    triggerHaptic(20);
    // Trigger the hidden file input directly within the user-gesture handler so
    // iOS Safari allows the photo picker to open without a separate tap.
    busterFileRefs.current[n.id]?.click();
  };

  const handleProofPick = async (n, file) => {
    if (!file) return;
    const callId = busterCallIdFromRef(n.ref);
    if (!callId) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Proof file must be under 25 MB.");
      return;
    }
    setBusterProofState((prev) => ({ ...prev, [n.id]: { submitting: true } }));
    const res = await dispatch(answerBusterCall({ id: callId, file }));
    if (answerBusterCall.fulfilled.match(res)) {
      toast.success(res.payload?.alreadyAnswered ? "Already answered!" : "Proof submitted! Pending President approval.");
      window.dispatchEvent(new CustomEvent("kindred:buster-call-answered", { detail: { callId } }));
      // The auto-dismiss listener removes this notification from the banner.
    } else {
      const msg = (typeof res.payload === "string" && res.payload) || "Couldn't submit proof. Try again.";
      toast.error(msg);
      setBusterProofState((prev) => ({ ...prev, [n.id]: { submitting: false } }));
    }
  };

  // Auto-dismiss buster call notification when proof is submitted through ANY path
  // (notification Show Support OR the buster call dialog card). Fires via
  // window event dispatched by BusterCallDialog's BusterCallCard on success.
  useEffect(() => {
    const handler = (e) => {
      const callId = e?.detail?.callId;
      if (!callId) return;
      const prefix = `buster_call:${callId}`;
      let removedIds = [];
      setItems((prev) => {
        const toRemove = prev.filter((n) => n.ref && n.ref.startsWith(prefix));
        removedIds = toRemove.map((n) => n.id);
        toRemove.forEach((n) => {
          dispatch(dismissNotification({ id: n.id }))
            .then(() => postSync({ type: "dismiss", id: n.id }))
            .catch(() => {});
        });
        return prev.filter((n) => !n.ref || !n.ref.startsWith(prefix));
      });
      if (removedIds.length > 0) {
        setBusterExpand((prev) => {
          const updated = { ...prev };
          removedIds.forEach((id) => delete updated[id]);
          return updated;
        });
      }
    };
    window.addEventListener("kindred:buster-call-answered", handler);
    return () => window.removeEventListener("kindred:buster-call-answered", handler);
  }, [dispatch, postSync]);

  // A Yes/No action button was clicked (Glitch verify). Optimistically stamp the
  // answer on the banner row so the buttons swap to "You answered"; reconcile
  // from the server on failure. The respond endpoint is idempotent server-side.
  const handleRespond = (n, value) => {
    triggerHaptic(12);
    setItems((prev) =>
      prev.map((x) => (x.id === n.id ? { ...x, responded_value: value } : x)),
    );
    dispatch(respondToNotification({ id: n.id, value }))
      .then(() => postSync({ type: "respond", id: n.id, value }))
      .catch(() => load());
  };

  // One-tap "Clear all" (Glitch #404). Optimistic so the whole stack vanishes
  // the instant it's tapped; reconcile from the server on failure. Mirrors the
  // same dismiss-all the Notifications page's "Mark all read" uses, and posts a
  // dismiss-all to sibling tabs so they clear too.
  const handleClearAll = () => {
    if (clearing || items.length === 0) return;
    triggerHaptic(16);
    setClearing(true);
    setItems([]);
    setRevealOpen(false);
    dispatch(dismissAllNotifications())
      .then(() => postSync({ type: "dismiss-all" }))
      .catch(() => load())
      .finally(() => setClearing(false));
  };

  // Inline reply to a glitch notification (thread #1338). Submits to
  // POST /glitch/portal/:id/reply-to-notify, then marks the row as "sent" so
  // the input swaps to a "Sent." confirmation. Dismiss happens separately.
  const handleGlitchReply = (n) => {
    const glitchId = glitchIdFromRef(n.ref);
    if (!glitchId) return;
    const text = (replyState[n.id]?.text || "").trim();
    if (!text) return;
    triggerHaptic(10);
    setReplyState((prev) => ({ ...prev, [n.id]: { ...prev[n.id], sending: true } }));
    dispatch(replyToGlitchNotify({ id: glitchId, body: text }))
      .then(() => {
        setReplyState((prev) => ({ ...prev, [n.id]: { text: "", sent: true, sending: false } }));
        // Dismiss the banner row since the submitter has replied.
        handleDismiss(n);
      })
      .catch(() => {
        setReplyState((prev) => ({ ...prev, [n.id]: { ...prev[n.id], sending: false } }));
      });
  };

  const goSettings = () => {
    triggerHaptic(10);
    navigate("/p/notifications?tab=settings");
  };

  const goInbox = () => {
    triggerHaptic(10);
    navigate("/p/notifications");
  };

  if (!token || items.length === 0) return null;

  const isGroup = items.length > 1;
  const hiddenCount = items.length - MAX_VISIBLE;
  const showReveal = hiddenCount > 0;
  const inlineReveal = hiddenCount <= MAX_INLINE_REVEAL;

  const toggleReveal = () => {
    triggerHaptic(8);
    setRevealOpen((v) => !v);
  };

  const visible = items.slice(0, MAX_VISIBLE);
  const extras = items.slice(MAX_VISIBLE);

  // Buster Call notification row — three-state expansion, no X dismiss.
  // state 0: normal view with Open + Show Support + X (X expands instead of dismissing)
  // state 1: "Please Show Support or Be Lame" prompt
  // state 2: "I do so hereby choose to Be Lame..." — next Be Lame = real dismiss
  const renderBusterCallRow = (n, topBorder) => {
    const expandState = busterExpand[n.id] || 0;
    const proofSubmitting = busterProofState[n.id]?.submitting || false;
    const onSwipeStart = (e) => {
      busterTouchRef.current[n.id] = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const onSwipeEnd = (e) => {
      const start = busterTouchRef.current[n.id];
      if (!start) return;
      const dx = e.changedTouches[0].clientX - start.x;
      const dy = e.changedTouches[0].clientY - start.y;
      delete busterTouchRef.current[n.id];
      if (expandState === 0 && (Math.abs(dx) > 45 || dy > 25)) {
        triggerHaptic(30);
        setBusterExpand((prev) => ({ ...prev, [n.id]: 1 }));
      }
    };

    return (
      <div
        key={n.id}
        className={`kp-notif-in overflow-hidden transition-all duration-300 ${topBorder ? "border-t border-white/5" : ""}`}
        onTouchStart={onSwipeStart}
        onTouchEnd={onSwipeEnd}
      >
        {/* Hidden file input — triggered directly from Show Support click so iOS
            opens the photo picker within the same user-gesture frame. */}
        <input
          type="file"
          accept="image/*,video/*,application/pdf"
          className="hidden"
          ref={(el) => { if (el) busterFileRefs.current[n.id] = el; else delete busterFileRefs.current[n.id]; }}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleProofPick(n, f); }}
        />
        {/* Base row: always visible */}
        <div className="px-3 py-2">
          <div className="flex items-start gap-3">
            <Flame className="h-4 w-4 mt-0.5 shrink-0 text-orange-400 animate-pulse" />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-semibold text-orange-400 min-w-0">{n.title}</div>
                {n.created_at && (
                  <span className="shrink-0 text-[10px] text-zinc-500 whitespace-nowrap">{formatWhen(n.created_at)}</span>
                )}
              </div>
              {n.body && (
                <div className="text-zinc-200 leading-snug whitespace-pre-wrap break-words text-sm">
                  {n.body}
                </div>
              )}
              {/* The two buttons used to sit here unexplained, and "Show Support"
                  read as an alternative to "Open" rather than the step after it.
                  Levi (thread 1a121f716c9243a3) asked for the walkthrough, so the
                  row now states the three steps in order with each step's button
                  beside it. */}
              <AnswerSteps
                company={n.buster_call_company}
                rating={n.buster_call_rating}
                opened={!!busterOpened[n.id]}
                submitting={proofSubmitting}
                onOpen={n.url ? () => {
                  triggerHaptic(15);
                  setBusterOpened((prev) => ({ ...prev, [n.id]: true }));
                  handleOpen(n.url);
                } : undefined}
                onShowSupport={() => handleShowSupport(n)}
              />
            </div>
            {expandState === 0 && (
              <button
                type="button"
                onClick={() => handleDismiss(n)}
                aria-label="Dismiss notification"
                title="Dismiss"
                className="shrink-0 p-1 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition active:scale-90"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Expansion level 1: "Please Show Support or Be Lame" */}
        {expandState >= 1 && (
          <div className="px-3 pb-3 bg-gradient-to-b from-zinc-900 to-zinc-800/60 transition-all duration-300">
            <div className="border border-orange-500/30 rounded-xl p-3 bg-orange-950/20 space-y-2.5">
              <p className="text-sm font-medium leading-snug text-center">
                <span className="text-emerald-300 font-bold">Please Show Support</span>
                <span className="text-zinc-400"> to receive Reward</span>
                <span className="text-zinc-500"> .. or </span>
                <span className="text-red-400 font-bold">Be Lame</span>
              </p>
              <div className="flex gap-2 justify-center flex-wrap">
                <button
                  type="button"
                  onClick={() => handleShowSupport(n)}
                  disabled={proofSubmitting}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-500/60 bg-emerald-500/20 px-4 py-2 text-sm font-bold text-emerald-300 hover:bg-emerald-500/30 active:scale-95 transition-all shadow shadow-emerald-500/10 disabled:opacity-60 disabled:pointer-events-none"
                >
                  {proofSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4 fill-current" />}
                  {proofSubmitting ? "Submitting…" : "Show Support"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    triggerHaptic(50);
                    setBusterExpand((prev) => ({ ...prev, [n.id]: 2 }));
                  }}
                  disabled={proofSubmitting}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm font-bold text-red-400 hover:bg-red-500/20 active:scale-95 transition-all disabled:opacity-40 disabled:pointer-events-none"
                >
                  😒 Be Lame
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expansion level 2: "I hereby choose to Be Lame" — next Be Lame = real dismiss */}
        {expandState >= 2 && (
          <div className="px-3 pb-4 transition-all duration-300">
            <div className="border border-red-500/25 rounded-xl p-4 bg-red-950/20 space-y-3">
              <p className="text-xs italic text-red-300/80 text-center leading-relaxed">
                "I do so herby choose to Be Lame rather than leave a review to Show Support to Kinfolk."
              </p>
              <div className="flex gap-2 justify-center flex-wrap">
                <button
                  type="button"
                  onClick={() => handleShowSupport(n)}
                  disabled={proofSubmitting}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-500/60 bg-emerald-500/20 px-4 py-2 text-sm font-bold text-emerald-300 hover:bg-emerald-500/30 active:scale-95 transition-all shadow shadow-emerald-500/10 disabled:opacity-60 disabled:pointer-events-none"
                >
                  {proofSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4 fill-current" />}
                  {proofSubmitting ? "Submitting…" : "Show Support"}
                </button>
                <button
                  type="button"
                  onClick={() => handleBusterCallDismiss(n)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-600 bg-zinc-800 px-4 py-2 text-sm font-bold text-zinc-500 hover:bg-zinc-700 hover:text-zinc-400 active:scale-95 transition-all"
                >
                  😔 Be Lame
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // One banner row (title/body/actions/Open/media/X). `topBorder` draws the
  // hairline that separates stacked rows from the one above.
  const renderRow = (n, topBorder) => (
    <div
      key={n.id}
      className={`px-3 py-2 kp-notif-in ${topBorder ? "border-t border-white/5" : ""}`}
    >
      <div className="flex items-start gap-3">
        <BellRing className="h-4 w-4 mt-0.5 shrink-0 text-cyan-400" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-semibold text-cyan-400 min-w-0">{n.title}</div>
            {n.created_at && (
              <span className="shrink-0 text-[10px] text-zinc-500 whitespace-nowrap">{formatWhen(n.created_at)}</span>
            )}
          </div>
          {n.body && (
            <div className="text-zinc-200 leading-snug whitespace-pre-wrap break-words">
              {n.body}
            </div>
          )}
          {Array.isArray(n.actions) && n.actions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {n.responded_value ? (
                <span
                  className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${
                    n.responded_value === "yes"
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : "border-red-500/40 bg-red-500/10 text-red-300"
                  }`}
                >
                  {`You answered: ${answeredLabel(n.actions, n.responded_value)}`}
                </span>
              ) : (
                n.actions.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => handleRespond(n, a.value)}
                    className={`inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition active:scale-95 ${
                      a.value === "yes"
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                        : "border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20"
                    }`}
                  >
                    {a.label || a.value}
                  </button>
                ))
              )}
            </div>
          )}
          {n.event_chat_host_event_id && (
            <button
              type="button"
              onClick={() => handleEventChatMute(n)}
              className="inline-flex items-center rounded-md border border-zinc-500/40 bg-zinc-500/10 px-2.5 py-0.5 mt-1 text-xs font-semibold text-zinc-200 transition active:scale-95 hover:bg-zinc-500/20"
            >
              {n.event_chat_muted ? "Turn on notifications from Event" : "Turn off notifications from Event"}
            </button>
          )}
          {/* Inline Respond for glitch notifications without Yes/No (thread #1338).
              Only shown to users who are the glitch submitter (the BFF guards it);
              the UI shows it for any glitch ref with no actions. */}
          {glitchIdFromRef(n.ref) && !(Array.isArray(n.actions) && n.actions.length) && (() => {
            const rs = replyState[n.id] || {};
            if (rs.sent) return null;
            return (
              <div className="flex items-end gap-1.5 mt-1.5">
                <input
                  type="text"
                  value={rs.text || ""}
                  onChange={(e) => setReplyState((prev) => ({ ...prev, [n.id]: { ...prev[n.id], text: e.target.value } }))}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleGlitchReply(n); } }}
                  placeholder="Respond…"
                  disabled={rs.sending}
                  className="flex-1 min-w-0 rounded-md border border-zinc-600 bg-zinc-800 px-2 py-1 text-xs text-white placeholder:text-zinc-500 focus:border-yellow-400/50 focus:outline-none disabled:opacity-50"
                />
                <button
                  type="button"
                  disabled={rs.sending || !rs.text?.trim()}
                  onClick={() => handleGlitchReply(n)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-md border border-yellow-400/40 bg-yellow-400/10 px-2 py-1 text-xs font-semibold text-yellow-200 hover:bg-yellow-400/20 disabled:opacity-40 disabled:pointer-events-none"
                >
                  {rs.sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  {rs.sending ? "" : "Send"}
                </button>
              </div>
            );
          })()}
          {n.url && (
            <button
              type="button"
              onClick={() => handleOpen(n.url)}
              className="inline-flex items-center gap-1 mt-0.5 text-xs font-medium text-cyan-300 hover:text-cyan-200"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </button>
          )}
          {Array.isArray(n.media_urls) && n.media_urls.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {n.media_urls.map((m, i) => (
                <BannerMediaThumb key={i} item={m} />
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => handleDismiss(n)}
          aria-label="Dismiss notification"
          title="Dismiss"
          className="shrink-0 p-1 rounded hover:bg-zinc-700 text-zinc-300 hover:text-white transition active:scale-90"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );

  const renderAnyRow = (n, topBorder) =>
    isBusterCall(n.ref) ? renderBusterCallRow(n, topBorder) : renderRow(n, topBorder);

  return (
    <div className="bg-zinc-900/95 text-white text-sm border-b border-cyan-500/25 shadow-lg shadow-black/20 kp-notif-in">
      <div className="max-w-screen-xl mx-auto">
        {/* Wraps at 320 px (the header used to overflow the screen by 7 px); buttons never split their label. */}
        {isGroup && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 border-b border-white/5 bg-zinc-900/60">
            <span className="font-semibold text-white">Notifications</span>
            <span className="rounded-full bg-cyan-500/15 px-2 py-0.5 text-[11px] font-semibold text-cyan-300">
              {items.length}
            </span>
            <span className="flex-1" />
            <button
              type="button"
              onClick={goSettings}
              aria-label="Edit Notification Settings"
              title="Edit Notification Settings"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-zinc-300 hover:bg-white/5 hover:text-white transition active:scale-95"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Edit settings
            </button>
            <button
              type="button"
              onClick={handleClearAll}
              disabled={clearing}
              aria-label="Clear all notifications"
              title="Clear all"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold text-cyan-300 hover:bg-cyan-500/15 transition active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
            >
              {clearing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCheck className="h-3.5 w-3.5" />
              )}
              Clear all
            </button>
          </div>
        )}

        {visible.map((n, i) => renderAnyRow(n, i > 0))}

        {/* The rest of the undismissed stack sits behind an expander so the top
            of the page can never be covered by a wall of banners. Modest
            backlogs (+N, up to MAX_INLINE_REVEAL) reveal inline, phone-shade
            style; a big backlog routes to the grouped Notifications page. */}
        {showReveal && inlineReveal && revealOpen && extras.map((n) => renderAnyRow(n, true))}

        {showReveal &&
          (inlineReveal ? (
            <button
              type="button"
              onClick={toggleReveal}
              aria-expanded={revealOpen}
              className={`w-full flex items-center justify-center gap-1.5 border-t border-white/5 px-3 py-1.5 text-xs font-medium text-cyan-300 hover:bg-white/5 transition active:scale-[0.99] ${
                revealOpen ? "border-t-0" : ""
              }`}
            >
              {revealOpen ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
              {revealOpen ? "Show less" : `${hiddenCount} more`}
            </button>
          ) : (
            <button
              type="button"
              onClick={goInbox}
              className="w-full flex items-center justify-center gap-1.5 border-t border-white/5 px-3 py-1.5 text-xs font-medium text-cyan-300 hover:bg-white/5 transition active:scale-[0.99]"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              See all {items.length} on the Notifications page
            </button>
          ))}
      </div>
    </div>
  );
};

export default NotificationBanner;
